import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  MapPin,
  ExternalLink,
  AlertCircle,
  Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import SEO, { companySchema } from "@/components/SEO";

const STATUS_TAG = {
  advancing: "bb-tag-positive",
  permitting: "bb-tag-neutral",
  delayed: "bb-tag-negative",
  operational: "bb-tag-positive",
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export default function CompanyDetailPage() {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [projects, setProjects] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    setCompany(null);
    setProjects([]);
    setArticles([]);

    api
      .get(`/companies/${slug}`)
      .then(async (r) => {
        if (!mounted) return;
        const c = r.data;
        setCompany(c);
        const [projRes, artRes] = await Promise.all([
          api.get("/projects"),
          api.get("/articles"),
        ]);
        if (!mounted) return;
        const proj = (Array.isArray(projRes.data) ? projRes.data : []).filter(
          (p) => p.company === c.id,
        );
        setProjects(proj);
        const arts = (Array.isArray(artRes.data) ? artRes.data : [])
          .filter((a) => a.company === c.id)
          .slice(0, 6);
        setArticles(arts);
      })
      .catch(() => mounted && setError(true))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div
        className="bb-container py-20 text-center bb-overline text-bb-slate"
        data-testid="company-loading"
      >
        Loading company…
      </div>
    );
  }

  if (error || !company) {
    return (
      <section className="bb-container py-20" data-testid="company-not-found">
        <div className="border border-border bg-white p-12 text-center max-w-2xl mx-auto">
          <AlertCircle className="text-bb-copper mx-auto" size={28} />
          <div className="bb-overline text-bb-copper mt-4">404 · Company</div>
          <h1 className="font-archivo text-3xl text-bb-navy mt-3 font-bold">
            Company not found.
          </h1>
          <Link
            to="/companies"
            className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm mt-6"
            data-testid="company-back-to-list"
          >
            <ArrowLeft size={14} /> Back to companies
          </Link>
        </div>
      </section>
    );
  }

  return (
    <article data-testid={`company-detail-${company.slug}`}>
      <SEO
        title={company.name}
        description={company.description}
        type="profile"
        jsonLd={companySchema(company, projects)}
      />
      {/* Hero */}
      <header className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <Link
            to="/companies"
            className="bb-link inline-flex items-center gap-2 text-xs font-archivo uppercase tracking-widest text-bb-navy mb-8"
            data-testid="company-back-link"
          >
            <ArrowLeft size={14} /> Back to Companies
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="bb-overline text-bb-copper">
                {company.ticker} · {company.exchange}
              </div>
              <h1
                className="bb-headline mt-4 text-3xl sm:text-4xl lg:text-5xl text-bb-navy"
                data-testid="company-title"
              >
                {company.name}
              </h1>
              <p className="mt-5 text-base lg:text-lg text-bb-slate leading-relaxed max-w-2xl">
                {company.description}
              </p>
              <div className="mt-6 flex items-center gap-6 flex-wrap text-xs font-mono text-bb-slate">
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={12} /> {company.headquarters}
                </span>
                {company.website && company.website !== "—" && (
                  <a
                    href={company.website}
                    target="_blank"
                    rel="noreferrer"
                    className="bb-link inline-flex items-center gap-1.5 text-bb-navy"
                    data-testid="company-website"
                  >
                    <ExternalLink size={12} />
                    {company.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-bb-offwhite border border-border p-6">
                <div className="bb-overline text-bb-slate mb-3">Coverage</div>
                <div className="flex flex-wrap gap-1.5">
                  {(company.commodities || []).map((c) => (
                    <span
                      key={c}
                      className="bb-tag-neutral text-[11px] font-mono px-2.5 py-1"
                    >
                      {c}
                    </span>
                  ))}
                </div>
                <div className="mt-5 grid grid-cols-2 gap-4">
                  <div>
                    <div className="bb-overline text-bb-slate">Projects</div>
                    <div className="font-mono text-2xl text-bb-navy mt-1 font-semibold">
                      {projects.length}
                    </div>
                  </div>
                  <div>
                    <div className="bb-overline text-bb-slate">In coverage</div>
                    <div className="font-mono text-2xl text-bb-navy mt-1 font-semibold">
                      {articles.length}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Projects */}
      <section className="py-12 lg:py-16" data-testid="company-projects-section">
        <div className="bb-container">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="bb-overline text-bb-copper">Project pipeline</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 font-bold tracking-tight">
                {company.name}&rsquo;s active projects
              </h2>
            </div>
          </div>

          {projects.length === 0 ? (
            <div className="border border-dashed border-border bg-white p-8 text-sm text-bb-slate font-mono">
              No projects currently tracked in BedrockBulletin&rsquo;s Canadian
              database for this operator.
            </div>
          ) : (
            <div className="bg-white border border-border overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border bg-bb-offwhite">
                    <th className="p-4 bb-overline text-bb-slate">Project</th>
                    <th className="p-4 bb-overline text-bb-slate">Province</th>
                    <th className="p-4 bb-overline text-bb-slate">Stage</th>
                    <th className="p-4 bb-overline text-bb-slate">Status</th>
                    <th className="p-4 bb-overline text-bb-slate">Capex</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p) => (
                    <tr
                      key={p.id}
                      className="border-b border-border last:border-b-0 hover:bg-bb-offwhite transition-colors"
                      data-testid={`company-project-${p.slug}`}
                    >
                      <td className="p-4 align-top">
                        <div className="font-archivo font-semibold text-bb-navy">
                          <Link to={`/projects/${p.slug}`} className="bb-link">
                            {p.name}
                          </Link>
                        </div>
                        <div className="text-xs text-bb-slate mt-1 font-mono">
                          {(p.commodity || []).join(" · ")}
                        </div>
                      </td>
                      <td className="p-4 align-top text-xs font-mono text-bb-slate">
                        {p.province}
                      </td>
                      <td className="p-4 align-top bb-overline text-bb-navy">
                        {p.stage}
                      </td>
                      <td className="p-4 align-top">
                        <span
                          className={`text-[11px] font-mono px-2 py-1 ${STATUS_TAG[p.status] || "bb-tag-neutral"}`}
                        >
                          {p.status}
                        </span>
                      </td>
                      <td className="p-4 align-top font-mono text-sm text-bb-navy">
                        {p.capexEstimate
                          ? `$${p.capexEstimate.toLocaleString()}M`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Coverage */}
      {articles.length > 0 && (
        <section
          className="py-14 lg:py-20 border-t border-border bg-white"
          data-testid="company-coverage-section"
        >
          <div className="bb-container">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="bb-overline text-bb-copper">
                  Coverage on the desk
                </div>
                <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 font-bold tracking-tight">
                  Recent dispatches
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articles.map((a) => (
                <Link
                  key={a.id}
                  to={`/news/${a.slug}`}
                  className="bb-card p-6 group flex flex-col gap-2"
                  data-testid={`company-article-${a.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="bb-overline text-bb-copper">
                      {a.category}
                    </span>
                    <span className="text-[11px] font-mono text-bb-slate">
                      {formatDate(a.publishedDate)}
                    </span>
                  </div>
                  <h3 className="font-archivo text-lg text-bb-navy font-semibold tracking-tight leading-snug">
                    <span className="bb-link">{a.title}</span>
                  </h3>
                  <p className="text-sm text-bb-slate leading-relaxed">
                    {a.excerpt}
                  </p>
                  <div className="mt-2 pt-3 border-t border-border flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-slate">
                      <Clock size={11} /> By {a.author}
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="text-bb-navy group-hover:text-bb-copper transition-colors"
                    />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
